/**
 * Create Task modal used from the single-order Tasks tab.
 *
 * @module createTaskModal
 */
import {
  SearchableSelect,
  TagFilterBar,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../enums/modalId";
import { useOncoemrUsers } from "../../../../../hooks/useOncoemrUsers";

/**
 * Form data entered by the user inside the create-task dialog.
 */
export interface CreateTaskFormData {
  taskName: string;
  taskNotes: string;
  status: string;
  recipientIds: string[];
  context: string;
}

/**
 * Props for {@link CreateTaskModal}.
 */
interface CreateTaskModalProps {
  onClose: () => void;
  onSubmit: (formData: CreateTaskFormData) => Promise<void>;
  isSubmitting?: boolean;
}

/**
 * Displays the create-task form inside the Oasis modal shell.
 *
 * @param props - Close handler, async submit callback, and optional loading flag.
 * @returns Task-creation dialog UI.
 */
const CreateTaskModal = ({
  onClose,
  onSubmit,
  isSubmitting = false,
}: CreateTaskModalProps) => {
  const { recipientOptions, loading: usersLoading } = useOncoemrUsers();
  const [formData, setFormData] = useState<
    Omit<CreateTaskFormData, "recipientIds">
  >({
    taskName: "",
    taskNotes: "",
    status: "",
    context: "",
  });
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [assigneeSelectKey, setAssigneeSelectKey] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const recipientLabelById = useMemo(
    () =>
      new Map(recipientOptions.map((option) => [option.value, option.label])),
    [recipientOptions],
  );

  const selectedRecipientTags = useMemo(
    () => recipientIds.map((id) => recipientLabelById.get(id) ?? id),
    [recipientIds, recipientLabelById],
  );

  const availableAssigneeOptions = useMemo(
    () =>
      recipientOptions.filter(
        (option) => option.value && !recipientIds.includes(option.value),
      ),
    [recipientIds, recipientOptions],
  );

  const statusOptions = useMemo(
    () => [
      { label: "Open", value: "open" },
      { label: "In Progress", value: "in-progress" },
    ],
    [],
  );

  const canSubmit = formData.taskName.trim().length > 0 && !isSubmitting;

  useEffect(() => {
    openModal(ModalId.CREATE_TASK_MODAL);
  }, []);

  /**
   * Validates the form, calls the parent submit handler, and closes on success.
   */
  const handleSave = async () => {
    if (!canSubmit) {
      return;
    }

    setSubmitError(null);

    try {
      await onSubmit({
        ...formData,
        recipientIds,
      });
      onClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create task";
      setSubmitError(message);
    }
  };

  /**
   * Removes a selected member when the user closes a tag chip.
   *
   * @param label - Display label of the tag to remove.
   */
  const handleRecipientTagRemove = (label: string) => {
    setRecipientIds((previous) =>
      previous.filter((id) => (recipientLabelById.get(id) ?? id) !== label),
    );
  };

  return (
    <Modal
      dialogId={ModalId.CREATE_TASK_MODAL}
      title="Create New Task"
      saveButtonText={isSubmitting ? "Creating…" : "Create"}
      cancelText="Cancel"
      onSave={() => {
        void handleSave();
      }}
      onClose={onClose}
      onCancel={onClose}
      disableSave={!canSubmit}
    >
      <div className="flex w-full min-w-[18rem] max-w-[34rem] flex-col gap-2 pb-2 pt-1">
        <label
          htmlFor="task-name"
          className="text-tiny font-semiBold leading-[1.125rem] text-primaryGray-2"
        >
          Task Name
        </label>
        <input
          id="task-name"
          value={formData.taskName}
          onChange={(event) =>
            setFormData((previous) => ({
              ...previous,
              taskName: event.target.value,
            }))
          }
          placeholder="Enter Task Header"
          disabled={isSubmitting}
          className="h-10 rounded border border-primaryGray-14 px-2 text-tiny font-semiBold leading-4 outline-none placeholder:text-primaryGray-7 focus:border-primaryGray-10 disabled:opacity-60"
        />

        <label
          htmlFor="task-notes"
          className="mt-0.5 text-tiny font-semiBold leading-[1.125rem] text-primaryGray-2"
        >
          Task Notes
        </label>
        <input
          id="task-notes"
          value={formData.taskNotes}
          onChange={(event) =>
            setFormData((previous) => ({
              ...previous,
              taskNotes: event.target.value,
            }))
          }
          placeholder="Enter Task Notes"
          disabled={isSubmitting}
          className="h-10 rounded border border-primaryGray-14 px-2 text-tiny font-semiBold leading-4 outline-none placeholder:text-primaryGray-7 focus:border-primaryGray-10 disabled:opacity-60"
        />

        <div className="mt-0.5">
          <SearchableSelect
            id="task-status"
            label="Status"
            options={statusOptions}
            value={formData.status}
            placeholder="Select"
            searchPlaceholder="Search status"
            onOptionChange={(option: { value: string }) => {
              setFormData((previous) => ({
                ...previous,
                status: option.value,
              }));
            }}
          />
        </div>

        <div className="mt-0.5">
          {selectedRecipientTags.length > 0 && (
            <TagFilterBar
              tags={selectedRecipientTags}
              closable
              showDropdown={false}
              singleLineScrollable
              className="create-task-modal__recipient-tags mb-1 max-w-full"
              onTagRemove={handleRecipientTagRemove}
            />
          )}
          <SearchableSelect
            key={assigneeSelectKey}
            id="task-assignee"
            label="Members/Assignee"
            options={availableAssigneeOptions}
            value=""
            placeholder={usersLoading ? "Loading members…" : "Select"}
            searchPlaceholder="Search members"
            resetValue
            shouldOmitPrevSelectedValueOnReset
            disabled={isSubmitting || usersLoading}
            onOptionChange={(option: { value: string }) => {
              if (!option.value || recipientIds.includes(option.value)) {
                return;
              }

              setRecipientIds((previous) => [...previous, option.value]);
              setAssigneeSelectKey((key) => key + 1);
            }}
          />
        </div>

        <label
          htmlFor="task-context"
          className="mt-0.5 text-tiny font-semiBold leading-[1.125rem] text-primaryGray-2"
        >
          Patient/Order Context
        </label>
        <input
          id="task-context"
          value={formData.context}
          onChange={(event) =>
            setFormData((previous) => ({
              ...previous,
              context: event.target.value,
            }))
          }
          placeholder="Enter Context"
          disabled={isSubmitting}
          className="h-10 rounded border border-primaryGray-14 px-2 text-tiny font-semiBold leading-4 outline-none placeholder:text-primaryGray-7 focus:border-primaryGray-10 disabled:opacity-60"
        />

        {submitError && (
          <p className="text-tiny text-red-600" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CreateTaskModal;
